import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, pluck, switchMap } from 'rxjs/operators';

import dayjs from 'dayjs';

import { Product, ProductCloseData } from '@shared/models/products.model';
import { ShortProductCard } from '@shared/models/short-product-card.model';

import { DocumentSetting } from '../payments/payment.model';
import { UtilsService } from '../utils/utils.service';
import { ProductsApiService } from './products-api.service';
import { ProductsTransformService } from './products-transform.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsCloseService {
  constructor(
    private productsApiService: ProductsApiService,
    private productsTransformService: ProductsTransformService,
    private utilsService: UtilsService
  ) {
    dayjs.locale('ru');
  }

  /**
   * Проверка возможности закрытия счета
   */
  public checkCloseAccountPossibility(product: Product): boolean {
    const accNumber = product.number;

    return (
      product.productType === 'account' &&
      (this.checkESAccount(accNumber) || product.isBrokerAccount || product.isMetallAccount)
    );
  }

  /**
   * Закрытие продукта
   */
  public closeProduct(
    id: string,
    products: Product[]
  ): Observable<{ result: boolean; product: Product; navParams: ProductCloseData }> {
    const navParams: ProductCloseData = {
      accIdCards: <ShortProductCard[]>null,
      endAccIdCards: <ShortProductCard[]>null,
      docSettings: <DocumentSetting[]>null,
      type: <'account' | 'deposit'>null,
      product: <Product>null
    };

    let closableProduct: Product;
    let zeroBalance = false;

    return this.getProduct(id, products).pipe(
      switchMap((product): Observable<ShortProductCard[]> => {
        closableProduct = product;

        navParams.type = product.productType as 'account' | 'deposit' | 'card';
        navParams.product = closableProduct;

        if (closableProduct.productType === 'account' && +product.balance === 0) {
          zeroBalance = true;

          return of([]);
        }

        let request: Observable<any>;

        switch (closableProduct.productType) {
          case 'account':

          case 'saveAccount':
            request = this.productsApiService.getAccountCloseData('products');
            break;

          case 'deposit':
            const currentDate = dayjs().format('YYYY-MM-DD');
            const remainingDays = dayjs(product.endDate, 'YYYY-MM-DD').diff(dayjs(currentDate, 'YYYY-MM-DD'), 'days');
            closableProduct.remainingDays = remainingDays.toString();
            request = this.productsApiService.getDepositCloseData('products');
            break;

          case 'card':
            request = this.productsApiService.getCardCloseData('products');
            break;
        }

        const filter = (products: Product[]): Product[] => {
          return products.filter((endProduct) => {
            return (
              endProduct.id !== product.id &&
              endProduct.number !== product.number &&
              !endProduct.isCollateralAccount &&
              (endProduct.currencyCode === product.currencyCode ||
                (endProduct.currencyCode === '810' && /^(40817)|(40820)|(42301)|(42601)/.test(endProduct.number)))
            );
          });
        };

        return request.pipe(
          map((res) => {
            const accIdProducts = this.productsTransformService
              .prepareProducts(res.accIdProducts)
              .filter((i) => i.id !== product.id);

            navParams.accIdCards = this.productsTransformService.generateShortProductCards(accIdProducts);

            return res;
          }),
          pluck('endAccIdProducts'),
          map((endAccIdProducts) => this.productsTransformService.prepareProducts(endAccIdProducts)),
          map(filter),
          map((filteredProducts) => this.productsTransformService.generateShortProductCards(filteredProducts))
        );
      }),
      switchMap((endAccIdCards) => {
        navParams.endAccIdCards = endAccIdCards;

        if ((!navParams.endAccIdCards.length && !zeroBalance) || (navParams.endAccIdCards.length && zeroBalance))
          return of({ docSettings: [] });

        switch (closableProduct.productType) {
          case 'account':

          case 'saveAccount':
            return this.productsApiService.getAccountCloseData('setup');

          case 'deposit':
            return this.productsApiService.getDepositCloseData('setup');

          case 'card':
            return this.productsApiService.getCardCloseData('setup');
        }
      }),
      switchMap((setup: { docSettings: DocumentSetting[] }) => {
        navParams.docSettings = this.utilsService.getArray(setup.docSettings);

        return of({
          result: zeroBalance ? true : !!navParams.endAccIdCards.length,
          product: closableProduct,
          navParams
        });
      })
    );
  }

  /**
   * Отображение ошибки закрытия продукта
   */
  public showProductCloseError(type: string, currencyCode: string): string {
    if (type === 'account') {
      return `Невозможно закрыть счет с данной валютой (${currencyCode}).`;
    }

    if (type === 'deposit') {
      return `Невозможно закрыть вклад с данной валютой (${currencyCode}).`;
    }

    if (type === 'card') {
      return `Невозможно закрыть карту с данной валютой (${currencyCode}).`;
    }

    return `Невозможно закрыть продукт с данной валютой (${currencyCode}).`;
  }

  /**
   * Проверка на ES-счет
   */
  private checkESAccount(number: string): boolean {
    return /^40724/.test(number);
  }

  /**
   * Получение продукта по ID
   */
  private getProduct(id: string, products: Product[]): Observable<Product> {
    return of(products.find((product) => product.id === id));
  }
}
